// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect } from 'react';

import { type Facility, useEvolveDelete, useEvolvePaginatedGet, useEvolvePost } from '@evolvemep/api-ts';
import { Loader, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { useWrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';

type Props = {
  facilityActionState: FacilityActionModalState | null;
  onClose: () => void;
  selectFacility: (facility: Facility | null) => void;
  refresh: () => void;
};

export type FacilityActionModalState = { action: 'delete' | 'open-close'; facility: Facility };

type OpenCloseFacilityFormValues = { newDefaultFacility: Facility | null };
type NewDefaultFormProps = {
  facility: Facility;
  value: Facility | null;
  onChange: (newValue: Facility | null) => void;
};

const SelectNewDefaultFacility = ({ facility, value, onChange }: NewDefaultFormProps) => {
  const fetchFacilities = useEvolvePaginatedGet('shop/facility');
  return (
    <SearchableSelect
      idKey="facilityId"
      paginatedGet={fetchFacilities}
      value={value}
      onChange={onChange}
      getItemLabel={(f) => f.facilityName}
      excludeOption={(o) => o.facilityId === facility.facilityId}
    />
  );
};

const OpenCloseFacilityContent = (props: NewDefaultFormProps) => {
  const { facility } = props;

  const {
    entireCount = 0,
    loading,
    fetchNextPage,
  } = useEvolvePaginatedGet('shop/project', {
    defaultConfig: {
      params: {
        defaultFacilityId: facility.facilityId,
      },
    },
  });
  useEffect(() => {
    if (!facility.isClosed) void fetchNextPage();
  }, [facility.isClosed, fetchNextPage]);

  const { setLoading } = useWrappedModal();
  useEffect(() => {
    setLoading(loading);
  }, [setLoading, loading]);

  if (loading) return null;

  return (
    <>
      <Text>
        Are you sure you want to {facility.isClosed ? 'activate' : 'archive'} <b>{facility.facilityName}</b>?
      </Text>
      {entireCount > 0 && (
        <>
          <Text>
            Archiving will remove this facility as a default for{' '}
            <b>
              {entireCount.toLocaleString()} project
              {entireCount > 1 && <>s</>}
            </b>
            . If you would like to reassign {entireCount === 1 ? 'this project' : 'these projects'} to a new facility,
            please select one below.
          </Text>
          <SelectNewDefaultFacility {...props} />
        </>
      )}
    </>
  );
};

const OpenCloseFacilityForm = ({
  facility,
  onUpdate,
}: {
  facility: Facility;
  onUpdate: (updated: Facility) => void;
}) => {
  const form = useForm<OpenCloseFacilityFormValues>({
    initialValues: { newDefaultFacility: null },
  });

  const { apiCall: closeCall } = useEvolvePost(`shop/facility/${facility.facilityId}/close`);
  const { apiCall: activateCall } = useEvolvePost(`shop/facility/${facility.facilityId}/activate`);

  const onSubmit = async (values: OpenCloseFacilityFormValues) => {
    const updated = facility.isClosed
      ? await activateCall({})
      : await closeCall({ newDefaultFacilityId: values.newDefaultFacility?.facilityId });
    notifications.show({
      title: `Successfully ${updated.isClosed ? 'archived' : 'activated'}`,
      message: `${updated.isClosed ? 'Archived' : 'Activated'} facility ${updated.facilityName}.`,
      color: 'green',
    });
    onUpdate(updated);
  };

  useFormModal(form, onSubmit);
  return (
    <OpenCloseFacilityContent
      facility={facility}
      value={form.values.newDefaultFacility}
      onChange={(v) => form.setFieldValue('newDefaultFacility', v)}
    />
  );
};

const OpenCloseFacilityModal = ({ facilityActionState, onClose, selectFacility, refresh }: Props) => {
  const facility = facilityActionState?.facility;

  return (
    <FormModal
      startDirty
      title={`${facility?.isClosed ? 'Activate' : 'Archive'} facility`}
      alertState={{
        title: 'Cannot archive facility',
        showAlert: (facility?.hasActiveWorkOrders && !facility.isClosed) ?? false,
        message: (
          <Text>
            <b>{facility?.facilityName}</b> contains active work orders and can not be archived.
          </Text>
        ),
      }}
      opened={facilityActionState?.action === 'open-close'}
      onClose={onClose}
      confirmationText={facility?.isClosed ? 'Activate' : 'Archive'}
      type="submit"
    >
      {facility === undefined ? (
        <Loader />
      ) : (
        <OpenCloseFacilityForm
          facility={facility}
          onUpdate={(f) => {
            selectFacility(f);
            refresh();
          }}
        />
      )}
    </FormModal>
  );
};

const DeleteFacilityModal = ({ facilityActionState, onClose, selectFacility, refresh }: Props) => {
  const { apiCall: deleteCall } = useEvolveDelete('shop/facility/:facilityId');
  return (
    <VariableTrackingModal
      title="Delete facility"
      alertState={{
        title: 'Cannot delete facility',
        showAlert: facilityActionState?.facility.hasWorkOrders ?? false,
        message: (
          <Text>
            <b>{facilityActionState?.facility.facilityName}</b> contains work orders and can not be deleted.
          </Text>
        ),
      }}
      variable={facilityActionState?.action === 'delete' ? facilityActionState.facility : null}
      onConfirm={async (f) => {
        await deleteCall({
          url: `shop/facility/${f.facilityId}`,
        });
        notifications.show({
          title: 'Successfully deleted',
          message: `Deleted facility ${f.facilityName}.`,
          color: 'green',
        });
        selectFacility(null);
        refresh();
      }}
      onClose={onClose}
      confirmationText="Delete"
    >
      {(f) => (
        <Text>
          Are you sure you want to delete <b>{f.facilityName}</b>?
        </Text>
      )}
    </VariableTrackingModal>
  );
};

export const FacilityActionModals = (props: Props) => (
  <>
    <DeleteFacilityModal {...props} />
    <OpenCloseFacilityModal {...props} />
  </>
);
